/**
 * Sentinel — SIU / Fraud-Waste-Abuse investigation command center.
 *
 * Access (matches the server):
 *   - Compliance officer + analyst (can_see_privacy) → full investigation workbench.
 *   - Department users → the narrow "records requested from your team" surface only.
 */
import { useState } from 'react';
import { Tooltip } from './ui';
import SentinelDashboard from './SentinelDashboard';
import SentinelQueue from './SentinelQueue';
import IntakeQueue from './IntakeQueue';
import CaseList from './CaseList';
import CaseWorkspace from './CaseWorkspace';
import SentinelWatchlist from './SentinelWatchlist';
import SentinelAudit from './SentinelAudit';
import SentinelReports from './SentinelReports';
import MyRecordRequests from './MyRecordRequests';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', tip: 'A one-glance view of your fraud program — open cases, the 60-day recovery clock, dollars identified vs. recovered, and referrals made.' },
  { id: 'queue',     label: 'Queue',     tip: 'Everything that needs attention right now, all in one list: new referrals waiting for a decision, cases that have gone quiet, and overpayments with the 60 day clock running down.' },
  { id: 'intake',    label: 'Intake',    tip: 'Where new referrals land — from the call center, Medicaid, CMS, a hotline tip, or an internal team. Paste a referral and the AI assistant cleans it into a case for you.' },
  { id: 'cases',     label: 'Cases',     tip: 'Every investigation, at every stage. Open one to work it: disposition it, request records, get AI help, recover money, and refer to the I-MEDIC.' },
  { id: 'watchlist', label: 'Watchlist', tip: 'Cases you are monitoring rather than investigating, sorted by when each is due for review.' },
  { id: 'reports',   label: 'Reports',   tip: 'State regulatory reporting. Florida\'s Annual FWA Activity Report (0133) is generated straight from your D-SNP and Medicaid cases: readiness check, annual figures, then export the agency\'s own template ready for SFTP submission.' },
  { id: 'audit',     label: 'Audit Trail', tip: 'A complete, tamper-proof record of every action taken in Sentinel — required for a CMS program audit and your SOC 2 review.' },
];

export default function SentinelCommandCenter({ user }) {
  const isCompliance = user?.can_see_privacy === true;
  const [view, setView] = useState('dashboard');
  const [caseId, setCaseId] = useState(null);
  const [showHelp, setShowHelp] = useState(() => !localStorage.getItem('sentinel-help-dismissed'));

  // Department users only ever see their records inbox — no case visibility.
  if (!isCompliance) {
    return (
      <div className="h-full flex flex-col bg-slate-50">
        <Header subtitle="Records requested from your team" />
        <div className="flex-1 overflow-auto">
          <MyRecordRequests user={user} />
        </div>
      </div>
    );
  }

  const openCase = (id) => { setCaseId(id); setView('case'); };
  const backToList = (toTab = 'cases') => { setCaseId(null); setView(toTab); };
  const goIntakeOpen = (id) => openCase(id);

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <Header subtitle="Special Investigations Unit — Fraud, Waste &amp; Abuse" />

      {/* Tabs */}
      <div className="px-6 bg-white border-b border-slate-200 flex items-center gap-1">
        {TABS.map((t) => (
          <Tooltip key={t.id} text={t.tip}>
            <button
              onClick={() => { setView(t.id); setCaseId(null); }}
              className={`relative px-4 py-3 text-sm font-medium transition-colors ${
                (view === t.id || (t.id === 'cases' && view === 'case'))
                  ? 'text-red-600' : 'text-slate-500 hover:text-slate-800'}`}
            >
              {t.label}
              {(view === t.id || (t.id === 'cases' && view === 'case')) && (
                <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-red-500 rounded-full" />
              )}
            </button>
          </Tooltip>
        ))}
      </div>

      {/* First-run help — friendly, dismissible */}
      {showHelp && (
        <div className="mx-6 mt-4 rounded-2xl bg-red-50 border border-red-100 px-5 py-4 flex items-start gap-3">
          <div className="text-sm text-red-900/90 leading-relaxed">
            <strong>Welcome to Sentinel.</strong> Here's the whole job in one line: a referral comes in →
            you decide if it's real → if not, the AI drafts a coaching note for whoever reported it → if real,
            you investigate (request records, get AI help, loop in a nurse/MD) → recover the money → refer to the I-MEDIC.
            Hover any <span className="font-semibold">label or “?”</span> for a plain-English explanation.
          </div>
          <button
            onClick={() => { setShowHelp(false); localStorage.setItem('sentinel-help-dismissed', '1'); }}
            className="ml-auto text-red-400 hover:text-red-700 text-sm font-medium flex-shrink-0"
          >Got it</button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {view === 'dashboard' && <SentinelDashboard onOpenCase={openCase} goTo={setView} />}
        {view === 'queue'     && <SentinelQueue onOpenCase={openCase} />}
        {view === 'intake'    && <IntakeQueue onOpenCase={goIntakeOpen} />}
        {view === 'cases'     && <CaseList onOpenCase={openCase} />}
        {view === 'watchlist' && <SentinelWatchlist onOpenCase={openCase} />}
        {view === 'reports'   && <SentinelReports onOpenCase={openCase} />}
        {view === 'audit'     && <SentinelAudit />}
        {view === 'case' && caseId && <CaseWorkspace caseId={caseId} onBack={() => backToList('cases')} user={user} />}
      </div>
    </div>
  );
}

function Header({ subtitle }) {
  return (
    <div className="px-6 py-4 bg-white border-b border-slate-200 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center flex-shrink-0">
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      </div>
      <div>
        <h1 className="text-lg font-bold text-slate-900 leading-tight">Sentinel</h1>
        <p className="text-xs text-slate-500" dangerouslySetInnerHTML={{ __html: subtitle }} />
      </div>
    </div>
  );
}
